import { BellOutlined } from '@ant-design/icons';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Drawer, Empty, Flex, List, Typography } from 'antd';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { formatDateTime } from '../../lib/format';
import { alertTarget, listInbox, markAllRead, markRead, unreadCount, type InboxItem } from './api';

const { Text } = Typography;

/**
 * The header bell: how many of my alerts are unread, and a drawer to read them.
 * The count refreshes every minute; the list only loads when the drawer opens.
 */
export function AlertsBell(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const count = useQuery({
    queryKey: ['inbox', 'unread-count'],
    queryFn: unreadCount,
    refetchInterval: 60_000,
  });
  const unread = count.data?.unread ?? 0;

  const inbox = useInfiniteQuery({
    queryKey: ['inbox', 'list'],
    queryFn: ({ pageParam }) => listInbox(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.hasMore && last.nextCursor !== null ? last.nextCursor : undefined),
    enabled: open,
  });
  const items = inbox.data?.pages.flatMap((p) => p.items) ?? [];

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['inbox'] });
  };

  const openItem = async (item: InboxItem): Promise<void> => {
    // Opening the record must not depend on the read mark landing.
    if (item.readAt === null) {
      try {
        await markRead(item.uid);
      } catch {
        // The badge corrects itself on the next refresh.
      }
      refresh();
    }
    const target = alertTarget(item);
    if (target !== null) {
      setOpen(false);
      void navigate(target);
    }
  };

  const readAll = async (): Promise<void> => {
    const newest = items[0];
    if (newest === undefined) return;
    try {
      await markAllRead(newest.createdAt);
    } finally {
      refresh();
    }
  };

  return (
    <>
      <Badge count={unread} size="small" offset={[-4, 4]}>
        <Button
          type="text"
          aria-label={unread > 0 ? t('inbox.bellUnread', { count: unread }) : t('inbox.bell')}
          icon={<BellOutlined />}
          onClick={() => {
            setOpen(true);
          }}
        />
      </Badge>
      <Drawer
        open={open}
        title={t('inbox.title')}
        onClose={() => {
          setOpen(false);
        }}
        width={420}
        className="nks-action-drawer"
        extra={
          unread > 0 && items.length > 0 ? (
            <Button onClick={() => void readAll()}>{t('inbox.markAllRead')}</Button>
          ) : null
        }
      >
        {items.length === 0 && !inbox.isLoading ? (
          <Empty description={t('inbox.empty')} />
        ) : (
          <List<InboxItem>
            loading={inbox.isLoading}
            dataSource={items}
            renderItem={(item) => (
              <List.Item
                style={{ cursor: 'pointer', minHeight: 44 }}
                role="button"
                tabIndex={0}
                onClick={() => void openItem(item)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    void openItem(item);
                  }
                }}
              >
                <Flex vertical gap={2} style={{ width: '100%' }}>
                  <Text strong={item.readAt === null}>{alertText(t, item)}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {formatDateTime(item.createdAt)}
                    {item.readAt === null ? ` · ${t('inbox.unread')}` : ''}
                  </Text>
                </Flex>
              </List.Item>
            )}
          />
        )}
        {inbox.hasNextPage && (
          <Button block size="large" loading={inbox.isFetchingNextPage} onClick={() => void inbox.fetchNextPage()}>
            {t('inbox.loadMore')}
          </Button>
        )}
      </Drawer>
    </>
  );
}

type Translate = ReturnType<typeof useTranslation>['t'];

const ISO_TIME = /^\d{4}-\d{2}-\d{2}T/;

/** Words an alert from its template and values; an alert this build does not know gets a plain line. */
function alertText(t: Translate, item: InboxItem): string {
  const values: Record<string, string> = {};
  for (const [key, value] of Object.entries(item.values)) {
    if (value === null) continue;
    values[key] = typeof value === 'string' && ISO_TIME.test(value) ? formatDateTime(value) : String(value);
  }
  // A digest names silent bridges only when there are some.
  const variant = item.templateCode === 'accounting.digest' && values['bridgesSilent'] !== undefined ? '_bridge' : '';
  const key = `inbox.alerts.${item.templateCode.replace('.', '_')}${variant}`;
  return t(key, { ...values, defaultValue: t('inbox.alerts.unknown') });
}
